import { Box, Typography } from '@mui/material';
import { TournamentStatus } from '@okletsplay/core';
import { TournamentEntity } from 'app/interfaces/entity';
import { TournamentStatusProgress } from 'modules/tournaments/components/TournamentStatus/TournamentStatusIndicator';
import { TournamentStatusLabel } from 'modules/tournaments/components/TournamentStatus/TournamentStatusLabel';
import { TournamentStatusViewType } from 'modules/tournaments/enums';
import { useTeamPlace } from 'modules/tournaments/hooks';
import moment from 'moment';
import React, { FC, ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { RemainingTime } from 'shared/components/RemainingTime';
import { TournamentTileDateStateContext } from 'modules/tournaments/providers';
import { TournamentTileDate } from '../TournamentTileDate';

interface OkLetsPlayTournamentStatusProps {
  tournament: TournamentEntity;
  viewType?: TournamentStatusViewType;
}

export const OkLetsPlayTournamentStatus: FC<OkLetsPlayTournamentStatusProps> = ({
  tournament,
  viewType = TournamentStatusViewType.Tile,
}) => {
  const { isDateHidden } = useContext(TournamentTileDateStateContext);
  const [isComplete, setIsComplete] = useState(false);
  const { teamPlace } = useTeamPlace(tournament);

  const isHeaderView = useMemo(() => viewType === TournamentStatusViewType.Header, [viewType]);

  const expirationTime = useMemo(
    () => (tournament?.matchSetupSettings?.maxDuration ?? 0) * 24 * 60 * 60,
    [tournament?.matchSetupSettings?.maxDuration]
  );

  const isExpirationTimeAvailable = useMemo(
    () => tournament.startedAt && expirationTime && tournament.status === TournamentStatus.Live,
    [expirationTime, tournament.startedAt, tournament.status]
  );

  const expirationDate = useMemo(() => {
    return moment(tournament.startedAt).add(expirationTime, 'seconds').toISOString();
  }, [tournament.startedAt, expirationTime]);

  const playersAmountLabel = useMemo(() => {
    if (!tournament) {
      return null;
    }

    return `Players: ${tournament.teams?.length} of ${tournament.teamsSettings?.maxTeams}`;
  }, [tournament]);

  const joinedTeamsSize = useMemo(() => tournament?.teams?.length ?? 0, [tournament?.teams]);

  const isStartedAtDateAvailable = useMemo(() => {
    return !isDateHidden && viewType === TournamentStatusViewType.Tile;
  }, [isDateHidden, viewType]);

  const headerLabelTemplate = useMemo<ReactElement | null>(() => {
    switch (tournament?.status) {
      case TournamentStatus.Pending:
        return <Typography variant={'subtitle2'}>{playersAmountLabel}</Typography>;
      case TournamentStatus.Live:
        return <Typography variant={'subtitle2'}>{playersAmountLabel}</Typography>;
      case TournamentStatus.Finished:
        return teamPlace ? (
          <Typography variant={'subtitle2'}>{`You placed: ${teamPlace}`}</Typography>
        ) : null;
      case TournamentStatus.Canceled:
      default:
        return null;
    }
  }, [playersAmountLabel, teamPlace, tournament?.status]);

  const onCompleteHandler = useCallback(() => setIsComplete(true), [setIsComplete]);

  const tileLabelTemplate = useMemo<ReactElement | null>(() => {
    switch (tournament?.status) {
      case TournamentStatus.Live:
        return (
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Typography variant={'subtitle2'}>{playersAmountLabel}</Typography>
            {isExpirationTimeAvailable && (
              <Typography variant="subtitle2" color="text.primary" display="flex">
                {'Ends in'}&nbsp; <RemainingTime date={expirationDate} />
              </Typography>
            )}
          </Box>
        );
      case TournamentStatus.Pending:
      case TournamentStatus.Checkin:
        return (
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Typography variant={'subtitle2'}>{playersAmountLabel}</Typography>
            {isStartedAtDateAvailable && <TournamentTileDate startedAt={tournament?.startedAt} />}
          </Box>
        );
      case TournamentStatus.Canceled:
      case TournamentStatus.Finished:
        return (
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            {Boolean(teamPlace) && (
              <Typography variant={'subtitle2'}>{`You placed: ${teamPlace}`}</Typography>
            )}
            {!Boolean(teamPlace) && (
              <Typography variant="subtitle2">{`Teams: ${joinedTeamsSize} of ${tournament?.teamsSettings?.maxTeams}`}</Typography>
            )}
            {isStartedAtDateAvailable && <TournamentTileDate startedAt={tournament?.startedAt} />}
          </Box>
        );
      default:
        return null;
    }
  }, [
    tournament,
    playersAmountLabel,
    isExpirationTimeAvailable,
    expirationDate,
    isStartedAtDateAvailable,
    teamPlace,
    joinedTeamsSize,
  ]);

  return (
    <Box minWidth={150} display="flex" flexDirection="column">
      {isHeaderView ? <>{headerLabelTemplate}</> : <>{tileLabelTemplate}</>}
      <TournamentStatusProgress
        status={tournament?.status}
        teamsSize={joinedTeamsSize}
        minTeamsSize={tournament?.teamsSettings?.minTeams}
        maxTeamsSize={tournament?.teamsSettings?.maxTeams}
      />
      <Box display="flex" flexDirection="row">
        {isHeaderView && isExpirationTimeAvailable && !isComplete && (
          <Typography variant="subtitle2" color="text.primary" display="flex">
            {'Ends in'}&nbsp; <RemainingTime date={expirationDate} onComplete={onCompleteHandler} />
          </Typography>
        )}
        {<TournamentStatusLabel status={tournament?.status} />}
      </Box>
    </Box>
  );
};
